import { datadogRum, type RumEvent } from '@datadog/browser-rum';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router';

import { useActiveChannel } from '~/context/PartnerConfigContext';
import { useLeadContext } from '~/provider/LeadProvider';
import { parseChannel } from '~/utils/channels';

export const DEFAULT_CHANNEL = 'brighte';

const RUM_ENABLED_STAGES = ['staging', 'uat', 'prod'];

const isRumEnabled = () => RUM_ENABLED_STAGES.includes(import.meta.env.VITE_STAGE);

// Read by `beforeSend` at assembly time rather than held in RUM's global context. The initial view
// event — the one that joins a landing page to the marketplace in a funnel — is assembled
// synchronously inside init(), before the SDK drains the pre-init call buffer that
// setGlobalContextProperty writes to, so global context never reaches it
// (DataDog/browser-sdk#3935, a regression from 6.6.3; we are on 6.25.2). A module ref set during
// render is the only way to tag that first event.
let currentChannel: string = DEFAULT_CHANNEL;

export const setCurrentChannel = (channel: string) => {
  currentChannel = channel;
};

export const addChannelToEvent = (event: RumEvent) => {
  event.context = { ...event.context, channel: currentChannel };
  return true;
};

export function initializeDatadogRum() {
  const VITE_STAGE = import.meta.env.VITE_STAGE;
  const VITE_DDRUM_APP_ID = import.meta.env.VITE_DDRUM_APPLICATION_ID;
  const VITE_DDRUM_CLIENT_TOKEN = import.meta.env.VITE_DDRUM_CLIENT_TOKEN;
  const GIT_COMMIT_SHA = import.meta.env.VITE_GIT_COMMIT_SHA;
  const baseUrl = import.meta.env.VITE_MARKETPLACE_BASE_URL;

  if (isRumEnabled()) {
    datadogRum.init({
      applicationId: VITE_DDRUM_APP_ID,
      clientToken: VITE_DDRUM_CLIENT_TOKEN,
      site: 'datadoghq.com',
      service: 'marketplace',
      env: VITE_STAGE,
      // Specify a version number to identify the deployed version of your application in Datadog
      version: GIT_COMMIT_SHA,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      defaultPrivacyLevel: 'mask-user-input',
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      silentMultipleInit: true,
      beforeSend: addChannelToEvent,
      allowedTracingUrls: [baseUrl],
      enableExperimentalFeatures: ['feature_flags'],
      // Premarketplace is embedded as a cross-origin iframe on partner sites (public/js/embed.js).
      // Default SameSite=Strict session cookies are blocked in that third-party context, so RUM
      // never establishes a session and dispatches nothing. A partitioned SameSite=None cookie
      // (CHIPS) works in the iframe; fall back to localStorage where cookies are blocked outright.
      usePartitionedCrossSiteSessionCookie: true,
      allowFallbackToLocalStorage: true,
    });
  }
}

export const DatadogRum = () => {
  const { lead } = useLeadContext();
  const activeChannel = useActiveChannel();
  const [searchParams] = useSearchParams();

  // Lead-first mirrors the routes (see status/_layout.tsx). The param sits ahead of the default so a
  // slug the enterprise API doesn't recognise — leaving activeChannel null — still reports what the
  // customer arrived with instead of being mislabelled 'brighte'. parseChannel normalises the empty
  // string (which the nuqs serializer emits for a null channel) back to null.
  const channel =
    parseChannel(lead?.channel ?? null) ??
    activeChannel?.slug ??
    parseChannel(searchParams.get('channel')) ??
    DEFAULT_CHANNEL;

  // Assigned during render, before init(), so beforeSend already has the channel when the SDK
  // assembles the initial view event.
  setCurrentChannel(channel);

  initializeDatadogRum();

  // The render-time assignment above is what the initial view event needs, but writing to a module
  // ref during render is impure — a concurrent render that React discards can still have written to
  // it. Re-asserting the committed value here makes the commit the authority.
  useEffect(() => {
    setCurrentChannel(channel);
  }, [channel]);

  useEffect(() => {
    if (lead?.id) {
      datadogRum.setUser({ id: lead.id });
    }
  }, [lead]);

  return null;
};
